/**
 * The ledger table: virtualized rows, dense columns, monospaced numerals.
 *
 * Only the visible window is in the DOM (plus a small overscan), so 5,000 rows scroll as
 * cheaply as 50. Rows are absolutely positioned inside a spacer of the full height,
 * which is what keeps the scrollbar honest.
 */

import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useMemo, useRef } from "react";

import { LockIcon } from "../../components/ui/icons";
import type { Member, Transaction } from "../../lib/api";
import { formatAmount, formatSigned, toneClass } from "../../lib/money";
import { cn } from "../../lib/utils";

export const ROW_HEIGHT = 40;
export const MOBILE_ROW_HEIGHT = 68;

/**
 * Sheet mode freezes date / item / total and scrolls the member columns past them.
 *
 * That only works with fixed pixel widths: `position: sticky` needs a `left` offset, and
 * an offset can only be written down if the columns to its left cannot change size. Hence
 * no `gap` in sheet mode either — the padding lives inside each cell, so the maths here
 * stays the simple running total below.
 */
const SHEET_DATE_W = 68;
const SHEET_ITEM_W = 232;
const SHEET_TOTAL_W = 104;
const SHEET_MEMBER_W = 96;
const SHEET_FROZEN_W = SHEET_DATE_W + SHEET_ITEM_W + SHEET_TOTAL_W;

interface Props {
  transactions: Transaction[];
  members: Member[];
  currency: string;
  myMemberId: number | null | undefined;
  categoryNames: Map<number, string>;
  sheetMode: boolean;
  activeIndex: number;
  selection: Set<number>;
  canSelect: boolean;
  onActivate: (index: number) => void;
  onOpen: (transaction: Transaction) => void;
  onToggleSelect: (id: number) => void;
  onReachEnd: () => void;
  isMobile: boolean;
}

function shortDate(iso: string): string {
  const [, month = "", day = ""] = iso.split("-");
  return `${Number(month)}/${Number(day)}`;
}

function longDate(iso: string): string {
  const date = new Date(`${iso}T00:00:00`);
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** Anything that is not a plain expense says so, in every view. */
function TypeBadge({ type }: { type: Transaction["type"] }) {
  if (type === "expense") return null;
  return (
    <span
      className={cn(
        "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide",
        type === "payment" ? "bg-accent/12 text-accent" : "bg-ink/[0.07] text-muted",
      )}
    >
      {type.replace("_", " ")}
    </span>
  );
}

export function LedgerTable({
  transactions,
  members,
  currency,
  myMemberId,
  categoryNames,
  sheetMode,
  activeIndex,
  selection,
  canSelect,
  onActivate,
  onOpen,
  onToggleSelect,
  onReachEnd,
  isMobile,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const names = useMemo(
    () => new Map(members.map((member) => [member.id, member.display_name])),
    [members],
  );
  const rowHeight = isMobile ? MOBILE_ROW_HEIGHT : ROW_HEIGHT;

  const virtualizer = useVirtualizer({
    count: transactions.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 12,
  });

  // Keep the keyboard cursor in view as j/k move it.
  useEffect(() => {
    if (activeIndex >= 0 && activeIndex < transactions.length) {
      virtualizer.scrollToIndex(activeIndex, { align: "auto" });
    }
  }, [activeIndex, transactions.length, virtualizer]);

  const items = virtualizer.getVirtualItems();
  const last = items.at(-1);
  useEffect(() => {
    if (last && last.index >= transactions.length - 10) onReachEnd();
  }, [last, transactions.length, onReachEnd]);

  // Item names get the slack, but categories and payers keep enough room to read.
  const gridTemplate = sheetMode
    ? `${SHEET_DATE_W}px ${SHEET_ITEM_W}px ${SHEET_TOTAL_W}px repeat(${members.length}, ${SHEET_MEMBER_W}px)`
    : canSelect
      ? "36px 60px minmax(12rem,2fr) minmax(7rem,1fr) minmax(8rem,1fr) 108px 108px 116px"
      : "60px minmax(12rem,2fr) minmax(7rem,1fr) minmax(8rem,1fr) 108px 108px 116px";
  // Sheet mode grows a column per member, so it needs a floor to scroll against.
  const minWidth = sheetMode ? SHEET_FROZEN_W + members.length * SHEET_MEMBER_W : undefined;

  /**
   * A frozen sheet cell. The background has to be opaque — a translucent tint would let
   * the member columns slide visibly underneath — so the hover and cursor states use the
   * solid `sunken` token rather than the transparent wash the scrolling cells get.
   */
  const frozen = (index: 0 | 1 | 2, active: boolean, extra?: string) =>
    cn(
      "sticky z-[1] flex h-full items-center px-2",
      active ? "bg-sunken" : "bg-raised group-hover:bg-sunken",
      index === 2 && "border-r border-rule",
      index === 0 && active && "shadow-[inset_2px_0_0_0_rgb(var(--accent))]",
      extra,
    );
  const frozenLeft = [0, SHEET_DATE_W, SHEET_DATE_W + SHEET_ITEM_W] as const;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-rule bg-raised">
      <div
        ref={scrollRef}
        className="scroll-region min-h-0 flex-1 overflow-auto"
        role="grid"
        aria-rowcount={transactions.length}
        tabIndex={-1}
      >
        {!isMobile && (
          <div
            role="row"
            className={cn(
              "sticky top-0 z-20 grid items-center border-b border-rule bg-sunken py-2.5",
              "text-2xs font-medium uppercase tracking-wider text-muted",
              sheetMode ? "" : "gap-3 px-4",
            )}
            style={{ gridTemplateColumns: gridTemplate, minWidth }}
          >
          {sheetMode ? (
            <>
              <span
                className="sticky z-[1] flex h-full items-center bg-sunken px-2"
                style={{ left: frozenLeft[0] }}
              >
                Date
              </span>
              <span
                className="sticky z-[1] flex h-full items-center bg-sunken px-2"
                style={{ left: frozenLeft[1] }}
              >
                Item
              </span>
              <span
                className="sticky z-[1] flex h-full items-center justify-end border-r border-rule bg-sunken px-2"
                style={{ left: frozenLeft[2] }}
              >
                Total
              </span>
              {members.map((member) => (
                <span
                  key={member.id}
                  className="truncate px-2 text-right"
                  title={member.display_name}
                >
                  {member.display_name}
                </span>
              ))}
            </>
          ) : (
            <>
              {canSelect ? <span aria-hidden /> : null}
              <span>Date</span>
              <span>Item</span>
              <span>Category</span>
              <span>Paid by</span>
              <span className="text-right">Total</span>
              <span className="text-right">Your share</span>
              <span className="text-right">Your net</span>
            </>
          )}
          </div>
        )}

        <div
          style={{
            height: virtualizer.getTotalSize(),
            position: "relative",
            width: "100%",
            minWidth,
          }}
        >
          {items.map((item) => {
            const transaction = transactions[item.index];
            if (!transaction) return null;
            const net = myMemberId ? (transaction.ledger[String(myMemberId)] ?? 0) : 0;
            const share =
              transaction.participants.find((participant) => participant.member_id === myMemberId)
                ?.share_base_cents ?? 0;
            const isActive = item.index === activeIndex;
            const isSelected = selection.has(transaction.id);

            return (
              <div
                key={transaction.id}
                role="row"
                aria-rowindex={item.index + 1}
                aria-selected={isActive}
                data-testid="ledger-row"
                onClick={() => {
                  onActivate(item.index);
                  onOpen(transaction);
                }}
                className={cn(
                  "group absolute left-0 top-0 w-full cursor-pointer border-b border-rule/60",
                  "bg-raised transition-colors duration-100 hover:bg-ink/[0.025]",
                  sheetMode ? "" : "px-4",
                  isActive && "bg-accent/[0.05] shadow-[inset_2px_0_0_0_rgb(var(--accent))]",
                  isSelected && "bg-accent/[0.08]",
                  transaction.deleted_at && "opacity-40 line-through",
                )}
                style={{ height: item.size, transform: `translateY(${item.start}px)` }}
              >
                {isMobile ? (
                  <div className="flex h-full flex-col justify-center gap-0.5">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="flex min-w-0 items-center gap-1.5 font-medium">
                        {transaction.locked ? <LockIcon /> : null}
                        <span className="truncate">{transaction.name}</span>
                        <TypeBadge type={transaction.type} />
                      </span>
                      <span className="shrink-0 text-2xs text-muted">
                        {transaction.payer_member_id
                          ? `${names.get(transaction.payer_member_id) ?? ""} paid`
                          : transaction.from_member_id && transaction.to_member_id
                            ? `${names.get(transaction.from_member_id) ?? ""} → ${
                                names.get(transaction.to_member_id) ?? ""
                              }`
                            : ""}
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between gap-3 text-xs text-muted">
                      <span className="truncate">
                        {longDate(transaction.occurred_on)}
                        {transaction.category_id
                          ? ` · ${categoryNames.get(transaction.category_id) ?? ""}`
                          : ""}
                      </span>
                      <span className="flex items-baseline gap-3">
                        <span className="num">
                          {formatAmount(transaction.total_base_cents, currency)}
                        </span>
                        <span className={cn("num font-medium", toneClass(net))}>
                          {net ? formatSigned(net, currency) : "—"}
                        </span>
                      </span>
                    </div>
                  </div>
                ) : (
                  <div
                    className={cn("grid h-full items-center text-sm", sheetMode ? "" : "gap-3")}
                    style={{ gridTemplateColumns: gridTemplate }}
                  >
                    {sheetMode ? (
                      <>
                        <span
                          className={frozen(0, isActive, "num text-xs text-muted")}
                          style={{ left: frozenLeft[0] }}
                        >
                          {shortDate(transaction.occurred_on)}
                        </span>
                        <span
                          className={frozen(1, isActive, "gap-1.5 overflow-hidden")}
                          style={{ left: frozenLeft[1] }}
                        >
                          {transaction.locked ? <LockIcon /> : null}
                          <span className="truncate">{transaction.name}</span>
                          <TypeBadge type={transaction.type} />
                        </span>
                        <span
                          className={frozen(2, isActive, "num justify-end")}
                          style={{ left: frozenLeft[2] }}
                        >
                          {formatAmount(transaction.total_base_cents, currency)}
                        </span>
                        {members.map((member) => {
                          const delta = transaction.ledger[String(member.id)];
                          return (
                            <span
                              key={member.id}
                              className={cn(
                                "num px-2 text-right text-xs",
                                delta ? toneClass(delta) : "text-muted/40",
                              )}
                            >
                              {delta ? formatAmount(delta, currency) : "·"}
                            </span>
                          );
                        })}
                      </>
                    ) : (
                      <>
                        {canSelect && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => onToggleSelect(transaction.id)}
                            aria-label={`Select ${transaction.name}`}
                            className="h-4 w-4 accent-[rgb(var(--accent))]"
                          />
                        )}
                        <span className="num text-xs text-muted">
                          {shortDate(transaction.occurred_on)}
                        </span>
                        <span className="flex items-center gap-1.5 truncate">
                          {transaction.locked ? <LockIcon /> : null}
                          <span className="truncate">{transaction.name}</span>
                          <TypeBadge type={transaction.type} />
                        </span>
                        <span className="truncate text-xs text-muted">
                          {categoryNames.get(transaction.category_id ?? -1) ?? ""}
                        </span>
                        <span className="truncate text-xs">
                          {names.get(transaction.payer_member_id ?? -1) ??
                            (transaction.from_member_id
                              ? `${names.get(transaction.from_member_id) ?? ""} → ${
                                  names.get(transaction.to_member_id ?? -1) ?? ""
                                }`
                              : "")}
                        </span>
                        <span className="num text-right">
                          {formatAmount(transaction.total_base_cents, currency)}
                        </span>
                        <span className="num text-right text-muted">
                          {share ? formatAmount(share, currency) : "—"}
                        </span>
                        <span className={cn("num text-right font-medium", toneClass(net))}>
                          {net ? formatSigned(net, currency) : "—"}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
